<script>
import {defineComponent} from 'vue'
import {useDeliveryStore} from '@js/store/DeliveryStore.js'

import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import IconPaymentMaestro from "@js/components/Icons/IconPaymentMaestro.vue";
import IconPaymentVisa from "@js/components/Icons/IconPaymentVisa.vue";
import IconPaymentMir from "@js/components/Icons/IconPaymentMir.vue";
import IconPaymentMasterCard from "@js/components/Icons/IconPaymentMasterCard.vue";
import IconPaymentSbp from "@js/components/Icons/IconPaymentSbp.vue";

import DeliveryZonesMap from "@js/components/Sections/Delivery/DeliveryZonesMap.vue";
import DeliveryCalculatorPanel from "@js/components/Sections/Delivery/DeliveryCalculatorPanel.vue";
import {checkDocument} from "@js/helpers.ts";

export default defineComponent({
    name: "DeliveryAndShippingPage",
    components: {
        DeliveryCalculatorPanel,
        DeliveryZonesMap,
        IconPaymentSbp,
        IconPaymentMasterCard,
        IconPaymentMir,
        IconPaymentVisa,
        IconPaymentMaestro,
        Breadcrumbs,
        MobileTopBar,
    },

    setup() {
        const delivery = useDeliveryStore()
        return {delivery}
    },

    data() {
        return {
            canLoadMap: false,
            selectedZone: {id: 1, name: 'Зона 1', coeff: 1.00},
            selectedPoint: null,
        };
    },

    methods: {
        onZoneChanged(z) {
            this.selectedZone = z;
            this.delivery.setZone(z);
        },
        onPointSelected(p) {
            this.selectedPoint = p;
            this.delivery.setCoords(p.coords);
        },
    },

    mounted() {
        if (checkDocument()) {
            this.canLoadMap = true
        }
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <h1 class="text-2xl md:text-3xl font-semibold mb-6">
            Доставка строительных материалов
        </h1>

        <section class="relative grid grid-cols-1 md:grid-cols-2 gap-5 mb-5 md:mb-10">
            <DeliveryZonesMap
                class="md:sticky md:top-24"
                v-if="canLoadMap"
                :zoom="8"
                :center="[55.462540, 37.781900]"
                @zone-changed="onZoneChanged"
                @point-selected="onPointSelected"
            />

            <div class="flex flex-col gap-5">
                <div class="bg-white rounded-3xl p-5 md:p-6 shadow-sm border">
                    <DeliveryCalculatorPanel v-if="canLoadMap"/>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Как проходит доставка
                    </h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>
                            Доставляем ЖБИ, бетонные смеси, пиломатериалы, тротуарную плитку, нерудные материалы и
                            строительные конструкции на объекты заказчиков.
                        </li>
                        <li>
                            Перед отправкой менеджер уточняет состав заказа, объем, адрес, удобный интервал приемки и
                            особенности подъезда к объекту.
                        </li>
                        <li>
                            Транспорт подбирается под конкретный материал - для ЖБИ и конструкций, сыпучих материалов,
                            пиломатериалов, плитки или бетонной смеси.
                        </li>
                        <li>
                            Если на объекте есть ограничения по въезду, высоте, весу транспорта или времени разгрузки,
                            сообщите об этом заранее.
                        </li>
                        <li>
                            Платная парковка, пропуска, въезды на закрытые территории и локальные сборы оплачиваются
                            заказчиком, если иное не согласовано отдельно.
                        </li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">
                        Информация носит справочный характер. Итоговые условия доставки фиксируются менеджером при
                        подтверждении заказа.
                    </p>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Из чего складывается стоимость доставки
                    </h2>
                    <p class="mb-4">
                        Стоимость зависит от адреса объекта, зоны доставки, объема партии, веса и габаритов материалов,
                        типа транспорта, количества точек разгрузки и выбранного временного интервала.
                    </p>
                    <p class="mb-2">
                        Калькулятор на странице помогает получить предварительный расчет. Финальная сумма уточняется
                        после проверки заказа, маршрута и условий разгрузки.
                    </p>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Доставка ЖБИ, плит и конструкций может требовать манипулятор или отдельную технику.</li>
                        <li>Сыпучие материалы доставляются транспортом, подходящим под объем и условия подъезда.</li>
                        <li>Бетонные смеси доставляются с учетом графика работ и времени приемки на объекте.</li>
                        <li>Для крупных партий и регулярных поставок возможны индивидуальные условия.</li>
                    </ul>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Требования к приемке и разгрузке
                    </h2>
                    <p class="mb-4">
                        Чтобы доставка прошла без задержек, подготовьте место для подъезда, разгрузки и складирования
                        материалов.
                    </p>
                    <div class="grid grid-cols-2 gap-3 text-sm">
                        <div class="bg-white rounded-xl p-3 border">
                            <div class="font-medium">ЖБИ и конструкции</div>
                            <div class="text-gray-600">
                                нужна безопасная зона разгрузки и доступ к месту складирования
                            </div>
                        </div>
                        <div class="bg-white rounded-xl p-3 border">
                            <div class="font-medium">Сыпучие материалы</div>
                            <div class="text-gray-600">
                                требуется место для выгрузки песка, щебня или других нерудных материалов
                            </div>
                        </div>
                    </div>
                    <ul class="list-disc list-outside ps-5 space-y-2 mt-3">
                        <li>На объекте должен быть ответственный представитель для приемки заказа.</li>
                        <li>Разгрузка выполняется в доступной и безопасной точке без нарушения ПДД и риска для техники.</li>
                        <li>Работы под ЛЭП, рядом с опасными зонами или при нестабильном грунте требуют отдельного согласования.</li>
                        <li>Если требуется подъем, перенос, перемещение по территории или спецтехника, это рассчитывается отдельно.</li>
                    </ul>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Документы и получение заказа
                    </h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Передаем документы по согласованной поставке: счет, УПД или товарную накладную.</li>
                        <li>Для юридических лиц при безналичной оплате может потребоваться доверенность или печать.</li>
                        <li>По отдельным материалам возможны паспорта качества, сертификаты или сопроводительные документы.</li>
                        <li>Проверьте номенклатуру, количество и состояние материалов до подписания документов.</li>
                    </ul>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Временные интервалы доставки
                    </h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Доставка выполняется в согласованный день и временной интервал.</li>
                        <li>Для некоторых заказов доступна доставка к определенному времени.</li>
                        <li>Время разгрузки, ожидания пропуска и подготовки площадки не входит в интервал доставки.</li>
                        <li>При сложном маршруте, пробках или нескольких рейсах время может быть скорректировано.</li>
                        <li>Для удаленных зон и крупных партий график согласовывается индивидуально.</li>
                    </ul>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Простой техники и дополнительные расходы
                    </h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Если техника ожидает разгрузку дольше согласованного времени, простой может оплачиваться отдельно.</li>
                        <li>Платная парковка, пропуска, въезд на закрытые территории и локальные сборы оплачиваются заказчиком.</li>
                        <li>Дополнительная точка разгрузки, изменение адреса или маршрута согласуются с менеджером.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">
                        Фактическая стоимость зависит от маршрута, типа транспорта, условий на площадке и времени работ.
                    </p>
                </div>

                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">
                        Если нужна разгрузка спецтехникой
                    </h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Для ЖБИ, плит, блоков и крупных конструкций может потребоваться кран-манипулятор или автокран.</li>
                        <li>Строповка выполняется только за штатные петли, закладные элементы или согласованные точки крепления.</li>
                        <li>Для длинномерных и тяжелых изделий заранее уточняются габариты, масса и место складирования.</li>
                        <li>Если подъезд ограничен, водитель разгружает материал в ближайшей безопасной доступной точке.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">
                        Опишите условия объекта при оформлении заявки - это поможет точнее подобрать транспорт и
                        рассчитать доставку.
                    </p>
                </div>
            </div>
        </section>
    </div>
</template>

